import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaFilter, FaGraduationCap } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import '../styles/MbaProjects.scss';
import { mbaAPI, getImageUrl } from '../utils/api';

const PAGE_SIZE = 20;

const MbaProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [types, setTypes] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [universityId, setUniversityId] = useState('');
  const [toast, setToast] = useState(null);
  const [noticeLang, setNoticeLang] = useState('en');

  const noticeText = {
    mr: {
      title: "महत्त्वाची सूचना",
      body: "वेबसाइटवर उपलब्ध असलेल्या सर्व MBA Projects मध्ये फक्त Word File (.docx) आणि PDF Report समाविष्ट आहेत. Company Certificate, Printing/Binding, Editing/Customization यांचा खर्च या किंमतीमध्ये समाविष्ट नाही. कृपया ऑर्डर करण्यापूर्वी याची नोंद घ्यावी।"
    },
    en: {
      title: "Important Notice",
      body: "All MBA Projects available on the website only include a Word File (.docx) and PDF Report. The cost of Company Certificate, Printing/Binding, Editing/Customization is not included in this price. Please note this before ordering."
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Load catalog (departments, types, universities)
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const [deptRes, typeRes, uniRes] = await Promise.all([
          mbaAPI.getDepartments(),
          mbaAPI.getTypes(),
          mbaAPI.getUniversities(),
        ]);
        setDepartments(Array.isArray(deptRes.data?.data) ? deptRes.data.data : []);
        setTypes(Array.isArray(typeRes.data?.data) ? typeRes.data.data : []);
        setUniversities(Array.isArray(uniRes.data?.data) ? uniRes.data.data : []);
      } catch (err) {
        setToast({ type: 'error', message: 'Failed to load filters.' });
      }
    };
    loadCatalog();
  }, []);

  // Fetch projects with real backend filtering
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {
          page,
          limit: PAGE_SIZE,
          sort_by: 'created_at',
          sort_dir: 'asc',
        };
        if (debouncedSearch) params.search = debouncedSearch;
        if (departmentId) params.department_id = departmentId;
        if (typeId) params.type_id = typeId;
        if (universityId) params.university_id = universityId;

        const res = await mbaAPI.getProjects(params);
        const data = res.data;
        setProjects(Array.isArray(data?.data) ? data.data : []);
        setPages(data?.pages || 1);
        setTotal(data?.total || 0);
      } catch (err) {
        setError('Unable to load MBA projects right now.');
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [page, debouncedSearch, departmentId, typeId, universityId]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(timer);
  }, [toast]);

  const showEmptyState = !loading && !error && projects.length === 0;

  const totalLabel = useMemo(() => {
    return `${total} Project${total === 1 ? '' : 's'}`;
  }, [total]);

  const handleUniversityCardClick = (uni) => {
    if (uni.slug) {
      navigate(`/mba-projects/${uni.slug}`);
    }
  };

  // University card logo render helper
  const renderUniLogo = (uni) => {
    const logo = uni.logo;
    if (!logo) return <span className="uni-card-emoji">🎓</span>;
    // If it's a URL path (starts with / or http)
    if (logo.startsWith('/') || logo.startsWith('http')) {
      return <img src={getImageUrl(logo)} alt={uni.name} className="uni-card-logo-img" />;
    }
    // Otherwise it's an emoji
    return <span className="uni-card-emoji">{logo}</span>;
  };

  return (
    <div className="mba-projects-page">
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Top Navigation Row */}
      <div className="back-link-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '1180px', margin: '0 auto 24px' }}>
        <button className="btn-back-link" onClick={() => navigate('/projects')}>
          <FaArrowLeft /> Back to Program
        </button>
        <button 
          className="btn-back-link" 
          onClick={() => navigate('/mba-guidelines')}
          style={{ 
            background: 'linear-gradient(135deg, #0f766e, #0d9488)', 
            color: 'white', 
            border: 'none',
            boxShadow: '0 4px 12px rgba(15, 118, 110, 0.2)'
          }}
        >
          <FaGraduationCap /> View University Guidelines
        </button>
      </div>

      {/* ===== IMPORTANT NOTICE BANNER ===== */}
      <div className="mba-notice-banner" style={{ 
        maxWidth: '1180px', 
        margin: '0 auto 24px', 
        padding: '16px 20px', 
        backgroundColor: '#fffbeb', 
        borderLeft: '5px solid #d97706', 
        borderTop: '1px solid #fef3c7',
        borderRight: '1px solid #fef3c7',
        borderBottom: '1px solid #fef3c7',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(217, 119, 6, 0.05)',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
          <h4 style={{ margin: 0, color: '#92400e', fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>📢</span> {noticeText[noticeLang].title}
          </h4>
          <div style={{ display: 'flex', background: '#f59e0b20', padding: '3px', borderRadius: '20px', border: '1px solid #f59e0b40' }}>
            <button 
              onClick={() => setNoticeLang('mr')}
              style={{
                background: noticeLang === 'mr' ? '#d97706' : 'transparent',
                color: noticeLang === 'mr' ? '#ffffff' : '#92400e',
                border: 'none',
                borderRadius: '15px',
                padding: '4px 12px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              मराठी
            </button>
            <button 
              onClick={() => setNoticeLang('en')}
              style={{
                background: noticeLang === 'en' ? '#d97706' : 'transparent',
                color: noticeLang === 'en' ? '#ffffff' : '#92400e',
                border: 'none',
                borderRadius: '15px',
                padding: '4px 12px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              English
            </button>
          </div>
        </div>
        <p style={{ margin: 0, color: '#78350f', fontSize: '14px', lineHeight: '1.6', fontWeight: '500' }}>
          {noticeText[noticeLang].body}
        </p>
      </div>

      {/* ===== FILTERS SECTION ===== */}
      <section className="mba-filters">
        <div className="filter-group">
          <label><FiSearch /> Search Title</label>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
            <input
              type="text"
              placeholder="Search MBA project titles…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{
                width: '100%',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '10px 30px 10px 12px',
                background: '#ffffff',
                color: '#0f172a',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: '14px',
              }}
            />
            {search && (
              <button
                onClick={() => {
                  setSearch('');
                  setPage(1);
                }}
                style={{
                  position: 'absolute',
                  right: '10px',
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: 0
                }}
                title="Clear Search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label><FaFilter /> University</label>
          <select
            value={universityId}
            onChange={(e) => {
              setUniversityId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Universities</option>
            {universities.map((uni) => (
              <option key={uni.id} value={uni.id}>
                {uni.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label><FaFilter /> Specialization</label>
          <select
            value={departmentId}
            onChange={(e) => {
              setDepartmentId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Specializations</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label><FaFilter /> Type</label>
          <select
            value={typeId}
            onChange={(e) => {
              setTypeId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Types</option>
            {Array.from(new Map(types.map(t => [t.name?.toLowerCase().trim(), t])).values()).map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>
      </section>

      {/* Clear Filters Button Row */}
      {(universityId || departmentId || typeId || search) && (
        <div style={{ maxWidth: '1180px', margin: '-12px auto 20px', padding: '0 8px', display: 'flex', justifyContent: 'flex-start' }}>
          <button
            className="btn-clear-filters"
            onClick={() => {
              setSearch('');
              setDepartmentId('');
              setTypeId('');
              setUniversityId('');
              setPage(1);
            }}
            style={{
              background: '#fee2e2',
              color: '#b91c1c',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(185, 28, 28, 0.08)'
            }}
          >
            ✕ Clear Filters
          </button>
        </div>
      )}

      {/* ===== PROJECT LISTING ===== */}
      <section className="mba-listing">
        <div className="list-header-wrap">
          <div className="list-title-group">
            <h2>Project Directory</h2>
            <p>Browse all MBA projects — filter by university, specialization, or type above.</p>
          </div>
          <div className="list-stats">
            <span>{totalLabel}</span>
            <span>{pages} page{pages === 1 ? '' : 's'}</span>
          </div>
        </div>

        {loading && (
          <div className="skeleton-grid">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="skeleton-card" />
            ))}
          </div>
        )}

        {error && (
          <div className="state state-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {showEmptyState && (
          <div className="state state-empty">
            <p>No MBA projects found for your filters.</p>
            <button
              onClick={() => {
                setSearch('');
                setDepartmentId('');
                setTypeId('');
                setUniversityId('');
                setPage(1);
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {!loading && !error && projects.length > 0 && (
          <div className="mba-table-container">
            <table className="mba-table">
              <thead>
                <tr>
                  <th className="th-sr">SR NO</th>
                  <th className="th-specialization">SPECIALIZATION</th>
                  <th className="th-type">TYPE</th>
                  <th className="th-title">PROJECT TITLE</th>
                  <th className="th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project, index) => (
                  <tr key={project.id || index}>
                    <td className="sr-no td-sr">{(page - 1) * PAGE_SIZE + index + 1}</td>
                    <td className="td-specialization">{project.specialization || '-'}</td>
                    <td className="td-type">{project.type || '-'}</td>
                    <td className="project-title-cell td-title">
                      <button
                        type="button"
                        className="project-title-link"
                        onClick={() => navigate(`/premium-preview/${project.id}?source=mba`)}
                      >
                        <strong>{project.title || 'Untitled MBA Project'}</strong>
                      </button>
                    </td>
                    <td className="actions-cell td-actions">
                      <div className="cell-actions">
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/premium-preview/${project.id}?source=mba`)}
                        >
                          🔎 VIEW MORE
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ===== PAGINATION ===== */}
      <footer className="mba-pagination">
        <button
          className="page-btn"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          <FaArrowLeft /> Previous
        </button>
        <div className="page-info">
          Page <strong>{page}</strong> of <strong>{pages}</strong>
        </div>
        <button
          className="page-btn"
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          disabled={page >= pages}
        >
          Next <FaArrowRight />
        </button>
      </footer>


    </div>
  );
};

export default MbaProjects;
